import { getMomentTz, MesfModal } from "@dexteel/mesf-core";
import { Box, InputAdornment, TextField, Typography } from "@mui/material";
import { useEffect, useState } from "react";

import { FooterModalButtons } from "@/controls/buttons/FooterModalButtons";
import { toFraction32 } from "@/utils/fraction";
import { InfoGrid, StateChip } from "../../components/primitives";
import type { SawQualityCheckRow } from "../../repositories/sawQualityChecksReportRepository";
import {
	deleteBundleQualityCheck,
	upsertBundleQualityCheck,
} from "../../repositories/sawWriteRepository";

const moment = getMomentTz();

const POSITIONS = [
	{ key: "TL", label: "Top Left" },
	{ key: "TR", label: "Top Right" },
	{ key: "MC", label: "Middle Center" },
	{ key: "BL", label: "Bottom Left" },
	{ key: "BR", label: "Bottom Right" },
] as const;
type Position = (typeof POSITIONS)[number]["key"];

// Bundle face on a 6-track grid: thirds on top (TL | MC | TR), halves below.
const GRID_POS: Record<Position, { gridColumn: string; gridRow: string }> = {
	TL: { gridColumn: "1 / span 2", gridRow: "1" },
	MC: { gridColumn: "3 / span 2", gridRow: "1" },
	TR: { gridColumn: "5 / span 2", gridRow: "1" },
	BL: { gridColumn: "1 / span 3", gridRow: "2" },
	BR: { gridColumn: "4 / span 3", gridRow: "2" },
};

const EMPTY_VALUES: Record<Position, string> = {
	TL: "",
	TR: "",
	MC: "",
	BL: "",
	BR: "",
};

const toNumber = (s: string): number | null => {
	if (s.trim() === "") return null;
	const n = Number(s);
	return Number.isFinite(n) ? n : null;
};

const valuesOf = (check: SawQualityCheckRow): Record<Position, string> => ({
	TL: check.CheckTL == null ? "" : String(check.CheckTL),
	TR: check.CheckTR == null ? "" : String(check.CheckTR),
	MC: check.CheckMC == null ? "" : String(check.CheckMC),
	BL: check.CheckBL == null ? "" : String(check.CheckBL),
	BR: check.CheckBR == null ? "" : String(check.CheckBR),
});

export type CheckModalMode = "edit" | "delete" | "";

type Props = {
	check: SawQualityCheckRow | null;
	mode: CheckModalMode;
	onClose: (shouldRefresh: boolean) => void;
	onError: (e: Error) => void;
};

export const SawQualityCheckModal = ({
	check,
	mode,
	onClose,
	onError,
}: Props) => {
	const [values, setValues] = useState<Record<Position, string>>(EMPTY_VALUES);
	const [saving, setSaving] = useState(false);
	const isDelete = mode === "delete";
	const open = mode !== "";

	useEffect(() => {
		setValues(check ? valuesOf(check) : EMPTY_VALUES);
	}, [check]);

	const outOfSpec = (raw: string): boolean => {
		const v = toNumber(raw);
		if (v == null || check?.MinLength == null || check?.MaxLength == null)
			return false;
		return v < check.MinLength || v > check.MaxLength;
	};

	const hasAnyValue = POSITIONS.some((p) => values[p.key].trim() !== "");

	const handleSubmit = async () => {
		if (!check) return;
		setSaving(true);
		try {
			if (isDelete) {
				await deleteBundleQualityCheck(check.QualityCheckId);
			} else {
				await upsertBundleQualityCheck({
					qualityCheckId: check.QualityCheckId,
					bundleId: check.BundleId,
					assetId: check.AssetId,
					checkTL: toNumber(values.TL),
					checkTR: toNumber(values.TR),
					checkMC: toNumber(values.MC),
					checkBL: toNumber(values.BL),
					checkBR: toNumber(values.BR),
					keepTimestamp: true,
				});
			}
			onClose(true);
		} catch (e) {
			onError(e as Error);
		} finally {
			setSaving(false);
		}
	};

	const bundleValue = () => {
		if (check?.BundleId == null) return "";
		if (check.IsCutback) return <StateChip label="Cutback" color="#757575" />;
		return check.BundleBarCount == null
			? `#${check.BundleId}`
			: `#${check.BundleId} · ${check.BundleBarCount}p`;
	};

	return (
		<MesfModal
			id="saw-quality-check"
			title={`${isDelete ? "DELETE" : "EDIT"} QUALITY CHECK`}
			open={open}
			handleClose={() => onClose(false)}
			maxWidth="sm"
		>
			<form
				onSubmit={(e) => {
					e.preventDefault();
					void handleSubmit();
				}}
			>
				<MesfModal.Content style={{ padding: "15px 30px" }}>
					{isDelete && (
						<Typography color="error" sx={{ mb: 2 }}>
							Are you sure you want to delete this quality check and its
							measurements?
						</Typography>
					)}

					<InfoGrid
						items={[
							{ label: "Line", value: check?.AssetName ?? "" },
							{
								label: "Checked",
								value: check
									? moment(check.CheckTime).format("HH:mm - DD/MM/YY")
									: "",
							},
							{ label: "Bundle", value: bundleValue() },
							{ label: "Part", value: check?.PartNumber ?? "" },
							{
								label: "Min/Max",
								value:
									check?.MinLength != null && check?.MaxLength != null
										? `${check.MinLength} / ${check.MaxLength}`
										: "",
							},
							{ label: "By", value: check?.CreatedBy ?? "" },
						]}
					/>

					{/* Bundle face, two flat rows: TL | MC | TR over BL | BR. */}
					<Box
						sx={{
							display: "grid",
							gridTemplateColumns: "repeat(6, 1fr)",
							gap: 0.8,
							mt: 2,
						}}
					>
						{POSITIONS.map((p) => (
							<Box key={p.key} sx={GRID_POS[p.key]}>
								<TextField
									size="small"
									fullWidth
									type="number"
									label={p.label}
									value={values[p.key]}
									disabled={isDelete}
									error={outOfSpec(values[p.key])}
									onChange={(e) =>
										setValues((prev) => ({ ...prev, [p.key]: e.target.value }))
									}
									slotProps={{
										htmlInput: { step: 0.01, inputMode: "decimal" },
										input: {
											endAdornment: (() => {
												const v = toNumber(values[p.key]);
												return v != null ? (
													<InputAdornment position="end">
														<Typography
															variant="caption"
															sx={{
																color: "text.disabled",
																whiteSpace: "nowrap",
															}}
														>
															{toFraction32(v)}
														</Typography>
													</InputAdornment>
												) : null;
											})(),
										},
									}}
								/>
							</Box>
						))}
					</Box>
				</MesfModal.Content>
				<MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
					<FooterModalButtons
						isSubmitLoading={saving}
						disabled={!isDelete && !hasAnyValue}
						onHide={() => onClose(false)}
						buttonLabel={isDelete ? "Delete" : "Save"}
						buttonColor={isDelete ? "secondary" : "primary"}
					/>
				</MesfModal.Actions>
			</form>
		</MesfModal>
	);
};
